// stores/auth.js
import { defineStore } from 'pinia';
import { api } from '../services/api';
import { usePlayerStore } from './player/playerStore';
import { useGamesStore } from './games/gamesStore';
import { useWebSocketStore } from './websocket/websocketStore';
import { useJackpotStore } from './jackpot/jackpotStore';
import { useProvidersStore } from './providers/providersStore';
import { useCategoriesStore } from './categories/categoriesStore';
import { useBannersStore } from './banners/bannersStore';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isLoading: false,
    isInitialized: false,
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!localStorage.getItem('token')
  }),

  actions: {
    // Mantenemos el nombre original de la función
    async initializeAuth() {
      // Si ya está inicializado o no hay token, no continuar
      if (this.isInitialized || !this.token) {
        return;
      }
    
      this.isLoading = true;
      console.log('🔍 initializeAuth: Iniciando con estado', {
        isInitialized: this.isInitialized,
        hasToken: !!this.token,
        hasUser: !!this.user
      });
    
      try {
        // Configurar headers de API
        api.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
        
        // Inicializar WebSocket primero y verificar conexión
        const webSocketStore = useWebSocketStore();
        const wsConnected = await webSocketStore.initialize();
        
        console.log('✅ WebSocket inicializado, verificando conexión:', {
          isConnected: webSocketStore.isConnected,
          hasEcho: !!webSocketStore.echo,
          connectionSuccess: wsConnected
        });
    
        // Si no se pudo conectar, reintentar una vez más
        if (!wsConnected) {
          console.log('🔄 Reintentando conexión WebSocket...');
          await webSocketStore.initialize();
        }
    
        if (this.user) {
          // Inicializar todos los stores necesarios de una vez
          const stores = {
            player: usePlayerStore(),
            games: useGamesStore(),
            providers: useProvidersStore(),
            categories: useCategoriesStore(),
            jackpot: useJackpotStore(),
            banners: useBannersStore()
          };
    
          await Promise.all([
            stores.player.getPlayerConfig(),
            stores.player.initialize(),
            stores.games.loadGames(),
            stores.categories.loadCategories(),
            stores.providers.loadProviders(),
            stores.jackpot.initialize(),
            stores.banners.initializeBanners()
          ]);
        }
    
        this.isInitialized = true;
        console.log('✅ Auth completamente inicializado');
      } catch (error) {
        console.error('Error in initializeAuth:', error);
        // Si hay error, limpiar todo y forzar re-login
        this.clearAuthenticatedUser();
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    

// Método register para añadir a tu stores/auth.js

// Añade este método dentro del objeto actions de tu store
// Método register para añadir a tu stores/auth.js

async register(userData) {
  if (this.isLoading) return { success: false };
  
  this.isLoading = true;
  try {
    const deviceInfo = {
      device_type: this.detectDeviceType(),
      browser: this.detectBrowser(),
      operating_system: this.detectOS()
    };
    
    const registerData = { ...userData, ...deviceInfo };
    
    // Usa la misma ruta que en login, ya que esa funciona
    await api.post('/player/register', registerData);
    
    return { 
      success: true, 
      message: 'Registro exitoso. Ahora puedes iniciar sesión.' 
    };
  } catch (error) {
    console.error('Register error:', error);
    throw error;
  } finally {
    this.isLoading = false;
  }
},

    async login(credentials, t) {
      if (this.isLoading) return;
      
      this.isLoading = true;
      try {
        // Capturar información del dispositivo
        const deviceInfo = {
          device_type: this.detectDeviceType(),
          browser: this.detectBrowser(),
          operating_system: this.detectOS()
        };

        console.log('Enviando información de dispositivo:', deviceInfo);
        
        // Combinar credenciales con info del dispositivo
        const loginData = { ...credentials, ...deviceInfo };
        
        const response = await api.post('/player/login', loginData);
        
        if (response.data.token) {
          // Guardar datos de autenticación
          this.token = response.data.token;
          this.user = response.data.user;
          this.isAuthenticated = true;
          
          // Almacenar la IP real recibida del backend (nuevo)
          if (response.data.playerIp) {
            localStorage.setItem('player_ip', response.data.playerIp);
          }
          
          // Persistir en localStorage
          localStorage.setItem('token', this.token);
          localStorage.setItem('user', JSON.stringify(this.user));
          
          // Inicializar la aplicación usando el nombre original
          await this.initializeAuth();
          
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        console.error('Login error:', error);
        
        if (error.response?.status === 403 && error.response?.data?.code === 'active_session') {
          return {
            success: false,
            isActiveSession: true,
            message: t('auth.errors.active_session')
          };
        }
    
        // Manejar errores de validación
        if (error.response?.status === 422) {
          const errorFields = error.response.data.errors;
          if (errorFields?.username) throw new Error(t('auth.errors.emptyUsername'));
          if (errorFields?.password) throw new Error(t('auth.errors.emptyPassword'));
        }
    
        const errorCode = error.response?.data?.code;
        if (errorCode) {
          const errorMessages = {
            user_not_found: 'user_not_found',
            invalid_password: 'invalid_password',
            account_banned: 'account_banned',
            invalid_credentials: 'invalid_credentials'
          };
          
          throw new Error(t(`auth.errors.${errorMessages[errorCode] || 'serverError'}`));
        }
    
        throw new Error(t('auth.errors.connectionError'));
      } finally {
        this.isLoading = false;
      }
    },
    async logout() {
      try {
        await api.post('/player/logout');
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        this.clearAuthenticatedUser();
        window.location.href = '/';
      }
    },

    clearAuthenticatedUser() {
      const webSocketStore = useWebSocketStore();
      webSocketStore.disconnect();
      
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;
      this.isInitialized = false;
  
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
    },
    // Métodos auxiliares
detectDeviceType() {
  if (/iPad|tablet|Tablet/i.test(navigator.userAgent)) return 'tablet';
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
},

detectBrowser() {
  const ua = navigator.userAgent;
  if (ua.match(/chrome|chromium|crios/i)) return "Chrome";
  if (ua.match(/firefox|fxios/i)) return "Firefox";
  if (ua.match(/safari/i)) return "Safari";
  if (ua.match(/opr\//i)) return "Opera";
  if (ua.match(/edg/i)) return "Edge";
  return "Unknown";
},

detectOS() {
  const ua = navigator.userAgent;
  if (ua.indexOf("Win") != -1) return "Windows";
  if (ua.indexOf("Mac") != -1) return "MacOS";
  if (ua.indexOf("Linux") != -1) return "Linux";
  if (ua.indexOf("Android") != -1) return "Android";
  if (ua.indexOf("iPhone") != -1 || ua.indexOf("iPad") != -1) return "iOS";
  return "Unknown";
}
  }
});